import { filter as _filter, map, hasIn, find, chain, cloneDeep, omit, get, upperCase, size, isEmpty, keys, isObject, forEach, isBoolean } from "lodash"
import update from "immutability-helper"
import { compareId } from "./securities"
import { FILTER_TYPES } from "./filters/chips"
import { STRING_OPERATORS } from "./filters/filter-ons/strings"
import { NUMERIC_OPERATORS } from "./filters/filter-ons/numbers"
import { NUMERIC_AND_EXISTENCE_OPERATORS } from "./filters/filter-ons/existence"
import { CLASSIFICATION_OPERATORS } from "./filters/filter-ons/classifications"
import { SECURITIES_OPERATORS } from "./filters/filter-ons/securities"

const EMPTY_ARRAY = []
export const LONG_SHORT = "longshort"
export const INTRADAY_PRICING = "intradayPricing"
export const INTRADAY_PRICING_FIELD = "hasIntradayPricing"
export const INTRADAY_PRICING_VALUES = {
  PRICED: "PRICED",
  UNPRICED: "UNPRICED",
}

export const factorSensitivityTypeMap = {
  predictedFactorSensitivity: "PREDICTED",
  historicalFactorSensitivity: "HISTORICAL",
}

export const isFilterValid = (filter) => {
  if (!filter.filterOn) return false
  if (!filter.filterOn.operators) return true
  if (!filter.operator) return false
  if (!filter.operator?.validator) return true
  return filter.operator.validator(filter.values)
}

function computeRestoreFilterOn(filterType, value) {
  let filterOn

  forEach(filterType, item => {
    const options = find(item.options, { value })
    if (options) {
      filterOn = options
      return false
    }
  })

  return filterOn
}

const computeAddMembershipFilter = (filterType, operator, id, dataType, values, filter) => {
  const subQuery = {
    ...operator.queryFormatter(id, operator.value, dataType, ...values),
    id,
  }

  if (!get(filter, filterType)) {
    return update(filter || {}, {
      [filterType]: { $set: [subQuery] },
    })
  }

  return update(filter, {
    [filterType]: { $push: [subQuery] },
  })
}

export function mapFactorExposureNegativeBoolOperator(filter) {
  return {
    ...filter,
    factorExposure: map(filter.factorExposure, expFilter => {
      const { id, contentSetId } = expFilter
      if (hasIn(expFilter, "hasNoValue")) {
        return { id, contentSetId, hasValue: false }
      }
      return expFilter
    }),
  }
}

// [TODO] This shouldn't be useful anymore
export const computeEditingFilter = (editingFilter, editing, filterOns, securitiesDescriptors) => {
  if (editing) {
    let filter
    let filterOn
    let values
    let operator

    const filterType = _filter(filterOns, filterOn => filterOn.type === editing.filterType)

    if (editing.filterType === "descriptor") {
      filter = editingFilter[editing.id]
      filterOn = computeRestoreFilterOn(filterType, editing.id)
    } else if (editing.filterType === LONG_SHORT) {
      filter = editingFilter[editing.id]
      filterOn = computeRestoreFilterOn(filterType, filter)
    } else if (editing.filterType === "securities") {
      filter = editingFilter[editing.filterType]
      filterOn = computeRestoreFilterOn(filterType, "securities")
    } else {
      filter = editingFilter[editing.filterType][editing.id]
      filterOn = computeRestoreFilterOn(filterType, filter.type || filter.id)
    }

    if (filterOn.operators) {
      operator = find(filterOn.operators, operator => filter[operator.value] !== undefined)

      switch (operator.selectorType) {
        case "securitiesSearch":
          values = map(
            filter[operator.value],
            (security) => ({
              ...security,
              country: chain(securitiesDescriptors)
                .find(security => compareId(security, security))
                .get("country")
                .value(),
            }),
          )
          break
        case "multiSelect":
          values = map(filter[operator.value], value => find(filterOn.options, o => o.value === value || o.value === value.value))
          break
        case "singleSelect":
          values = [find(filterOn.options, v => v.value === filter[operator.value] || v.value === filter[operator.value].value)]
          break
        case "range":
          values = map(filter[operator.value], value => value)
          break
        default:
          values = [filter[operator.value]]
          break
      }
    }

    if (filterOn.dataType === "millions") {
      values = map(values, value => value / 1000000)
    }

    return {
      descriptor: editing.id,
      filter: {
        filterOn,
        operator,
        values,
      },
    }
  }

  return null
}

export const computeRawFilter = (editingFilter, editing, filterOns, securitiesDescriptors) => {
  if (editing) {
    let filter
    let filterOn
    let values
    let operator
    let isUnsupported = false

    const filterOnByType = filterOns.filter(filterOn => filterOn.type === editing.filterType)

    if (editing.filterType === "descriptor") {
      filter = editingFilter[editing.id]
      filterOn = computeRestoreFilterOn(filterOnByType, editing.id)
    } else if (editing.filterType === LONG_SHORT) {
      filter = editingFilter[editing.id]
      filterOn = computeRestoreFilterOn(filterOnByType, filter)
    } else if (editing.filterType === INTRADAY_PRICING) {
      filter = editingFilter[editing.id] ? INTRADAY_PRICING_VALUES.PRICED : INTRADAY_PRICING_VALUES.UNPRICED
      filterOn = computeRestoreFilterOn(filterOnByType, filter)
      if (filterOn?.isUnsupported) {
        isUnsupported = true
      }
    } else if (editing.filterType === "securities") {
      filter = editingFilter[editing.filterType]
      filterOn = computeRestoreFilterOn(filterOnByType, "securities")
    } else if (["predictedFactorSensitivity", "historicalFactorSensitivity"].includes(editing.filterType)) {
      filter = get(editingFilter, ["factorSensitivity", editing.id], {})
      filterOn = computeRestoreFilterOn(filterOnByType, filter.id)
    } else {
      filter = get(editingFilter, [editing.filterType, editing.id], {})
      filterOn = computeRestoreFilterOn(filterOnByType, filter.type || filter.id)
    }

    if (!filterOn) {
      isUnsupported = true
      filterOn = createFallbackFilterOn(editing.filterType, [LONG_SHORT, INTRADAY_PRICING].includes(editing.filterType) ? filter : editing.id)
    }

    if (filterOn && filterOn.operators) {
      operator = find(filterOn.operators, operator => filter[operator.value] !== undefined)

      if (operator && !isUnsupported) {
        switch (operator.selectorType) {
          case "securitiesSearch":
            values = map(
              filter[operator.value],
              (security) => ({
                ...security,
                country: chain(securitiesDescriptors)
                  .find(security => compareId(security, security))
                  .get("country")
                  .value(),
              }),
            )
            break
          case "multiSelect":
            values = map(filter[operator.value], value => find(filterOn.options, o => o.value === value || o.value === value.value))
            break
          case "singleSelect":
            values = [find(filterOn.options, v => v.value === filter[operator.value] || v.value === filter[operator.value].value)]
            break
          case "range":
            values = map(filter[operator.value], value => value)
            break
          default:
            values = [filter[operator.value]]
            break
        }
      } else if ([LONG_SHORT, INTRADAY_PRICING].includes(editing.filterType)) {
        values = [filter]
      }
    }

    if (filterOn?.dataType === "millions") {
      values = map(values, value => value / 1000000)
    }

    if (isUnsupported) {
      if ([LONG_SHORT, INTRADAY_PRICING].includes(editing.filterType)) {
        values = [filter]
      } else if (operator) {
        const rawValue = filter[operator.value]
        const rawValues = Array.isArray(rawValue) ? rawValue : [rawValue]

        switch (operator.selectorType) {
          case "multiSelect":
          case "singleSelect":
            values = rawValues.map(val => {
              if (isObject(val) && val.value !== undefined) {
                return val
              }
              return { value: val, label: val }
            })
            break
          case "securitiesSearch":
            values = rawValues.map(val => {
              if (isObject(val) && (val.ticker || val.id)) {
                return val
              }
              return { value: val, label: val }
            })
            break
          case "range":
          default:
            values = rawValues
            break
        }
      }
    }

    return {
      descriptor: editing.id,
      filter: {
        filterOn,
        operator,
        values: values || [],
      },
      isUnsupported,
    }
  }

  return null
}

function createFallbackFilterOn(filterType, id) {
  const filterTypeDef = FILTER_TYPES.find(ft => ft.field === id)
  const getLabel = () => {
    if (filterTypeDef?.label) {
      if (typeof filterTypeDef.label === "object") {
        return filterTypeDef.label[id] || id
      }
      return filterTypeDef.label
    }
    return id
  }

  switch (filterType) {
    case "descriptor":
      return {
        label: getLabel(),
        type: "descriptor",
        dataType: "string",
        value: id,
        operators: STRING_OPERATORS,
        options: [],
      }
    case "securities":
      return {
        id: "securities",
        label: "Securities",
        type: "securities",
        dataType: "string",
        value: "securities",
        operators: SECURITIES_OPERATORS,
        options: [],
      }
    case "classification":
      return {
        label: "Classification",
        type: "classification",
        dataType: "string",
        value: id,
        operators: CLASSIFICATION_OPERATORS,
        options: [],
      }
    case "risk":
      return {
        label: "Risk",
        type: "risk",
        dataType: "percent",
        value: id,
        operators: NUMERIC_OPERATORS,
        options: [],
      }
    case "factorExposure":
      return {
        label: "Factor Exposure",
        type: "factorExposure",
        dataType: "numeric",
        value: id,
        operators: NUMERIC_AND_EXISTENCE_OPERATORS,
        options: [],
      }
    case "factorSensitivity":
      return {
        label: "Factor Sensitivity",
        type: "factorSensitivity",
        dataType: "numeric",
        value: id,
        operators: NUMERIC_AND_EXISTENCE_OPERATORS,
        options: [],
      }
    case "beta":
      return {
        label: "Beta",
        type: "beta",
        dataType: "numeric",
        value: id,
        operators: NUMERIC_OPERATORS,
        options: [],
      }
    case "tag":
      return {
        label: "Tags",
        type: "tag",
        dataType: "string",
        value: id,
        operators: STRING_OPERATORS,
        options: [],
      }
    case "positionAttributes":
      return {
        id: id,
        label: "Position Attributes",
        type: "positionAttributes",
        dataType: "string",
        value: id,
        operators: STRING_OPERATORS,
        options: [],
      }
    case LONG_SHORT:
      return {
        label: id === "LONG" ? "Long" : id === "SHORT" ? "Short" : "Long/Short",
        type: LONG_SHORT,
        dataType: "constant",
        value: id,
        operators: [],
        options: [],
      }
    case INTRADAY_PRICING:
      return {
        label: id === INTRADAY_PRICING_VALUES.PRICED ? "Priced Intraday" : "Not Priced Intraday",
        type: INTRADAY_PRICING,
        dataType: "constant",
        value: id,
        operators: [],
        options: [],
      }
    default:
      return {
        label: getLabel(),
        type: filterType,
        dataType: "string",
        value: id,
        operators: STRING_OPERATORS,
        options: [],
      }
  }
}

const SECURITIES_FILTER_FIELDS = ["ticker", "mic", "country", "indexId", "internalId"]

export function formatSecurityForFilter(security, includeAssetClass = true) {
  const securitiesFields = includeAssetClass
    ? [...SECURITIES_FILTER_FIELDS, "assetClass"]
    : SECURITIES_FILTER_FIELDS
  return chain(security)
    .pick(securitiesFields)
    .omitBy(fieldValue => !fieldValue)
    .value()
}

export function computeAddFilter(filter, type, dataType, id, operator, values, contentSetId) {
  switch (type) {
    case LONG_SHORT:
      filter = {
        ...filter,
        longShort: id,
      }
      break
    case INTRADAY_PRICING:
      filter = {
        ...filter,
        [INTRADAY_PRICING_FIELD]: id === INTRADAY_PRICING_VALUES.PRICED,
      }
      break
    case "descriptor":
      filter = update(filter, {
        [id]: { $set: operator.queryFormatter(id, operator.value, dataType, ...values) },
      })
      break
    case "risk": {
      const subQuery = operator.queryFormatter(id, operator.value, dataType, ...values)
      subQuery.type = id
      if (!get(filter, "risk")) {
        filter = update(filter, {
          risk: { $set: [subQuery] },
        })
      } else {
        filter = update(filter, {
          risk: { $push: [subQuery] },
        })
      }
      break
    }
    case "factorExposure": {
      const subQuery = operator.queryFormatter(id, operator.value, dataType, ...values)
      subQuery.id = id
      if (contentSetId) {
        subQuery.contentSetId = contentSetId
      }

      if (!get(filter, "factorExposure")) {
        filter = update(filter, {
          factorExposure: { $set: [subQuery] },
        })
      } else {
        filter = update(filter, {
          factorExposure: { $push: [subQuery] },
        })
      }
      break
    }
    case "predictedFactorSensitivity":
    case "historicalFactorSensitivity": {
      const subQuery = operator.queryFormatter(id, operator.value, dataType, ...values)
      subQuery.id = id
      subQuery.type = get(factorSensitivityTypeMap, type)

      if (!get(filter, "factorSensitivity")) {
        filter = update(filter, {
          factorSensitivity: { $set: [subQuery] },
        })
      } else {
        filter = update(filter, {
          factorSensitivity: { $push: [subQuery] },
        })
      }
      break
    }
    case "universe": {
      const subQuery = {
        type: upperCase(id),
        [operator.value]: values[0],
      }

      if (!get(filter, "universe")) {
        filter = update(filter, {
          universe: { $set: [subQuery] },
        })
      } else {
        filter = update(filter, {
          universe: { $push: [subQuery] },
        })
      }
      break
    }
    case "securities": {
      const formattedSecurities = map(values, (value) => formatSecurityForFilter(value))

      const subQuery = {
        [operator.value]: formattedSecurities,
      }

      if (!get(filter, "securities")) {
        filter = update(filter, {
          securities: { $set: subQuery },
        })
      } else {
        filter = update(filter, {
          securities: { $merge: subQuery },
        })
      }
      break
    }
    case "beta": {
      let subQuery = operator.queryFormatter(id, operator.value, dataType, ...values)
      subQuery = {
        [id]: subQuery,
      }
      if (!get(filter, "beta")) {
        filter = update(filter, {
          beta: { $set: subQuery },
        })
      } else {
        filter = update(filter, {
          beta: { $merge: subQuery },
        })
      }
      break
    }
    case "classification": {
      const subQuery = {
        ...operator.queryFormatter(id, operator.value, dataType, ...values),
        id,
      }
      if (!get(filter, "classification")) {
        filter = update(filter, {
          classification: { $set: [subQuery] },
        })
      } else {
        filter = update(filter, {
          classification: { $push: [subQuery] },
        })
      }
      break
    }
    case "tag":
    case "positionAttributes": {
      filter = computeAddMembershipFilter(type, operator, id, dataType, values, filter)
      break
    }
  }

  return filter
}

export function computeRemoveFilter(originalFilter, filterType, id) {
  let filter = cloneDeep(originalFilter)
  switch (filterType) {
    case LONG_SHORT:
    case INTRADAY_PRICING:
    case "descriptor":
      filter = omit(filter, id)
      break
    case "risk": {
      filter = update(filter, { risk: { $splice: [[id, 1]] } })
      if (filter.risk.length === 0) {
        delete filter.risk
      }
      break
    }
    case "factorExposure": {
      filter = update(filter, { factorExposure: { $splice: [[id, 1]] } })
      if (filter.factorExposure.length === 0) {
        delete filter.factorExposure
      }
      break
    }
    case "predictedFactorSensitivity":
    case "historicalFactorSensitivity": {
      filter = update(filter, { factorSensitivity: { $splice: [[id, 1]] } })
      if (filter.factorSensitivity.length === 0) {
        delete filter.factorSensitivity
      }
      break
    }
    case "universe": {
      filter = update(filter, { universe: { $splice: [[id, 1]] } })
      if (filter.universe.length === 0) {
        delete filter.universe
      }
      break
    }
    case "securities": {
      filter = omit(filter, `securities.${id}`)
      if (size(filter.securities.in) === 0 && size(filter.securities.notIn) === 0) {
        delete filter.securities
      }
      break
    }
    case "beta": {
      filter = omit(filter, `beta.${id}`)
      if (isEmpty(filter.beta)) {
        delete filter.beta
      }
      break
    }
    case "classification": {
      filter = update(filter, { classification: { $splice: [[id, 1]] } })
      if (filter.classification.length === 0) {
        delete filter.classification
      }
      break
    }
    case "tag": {
      filter = update(filter, { tag: { $splice: [[id, 1]] } })
      if (size(filter.tag) === 0) {
        delete filter.tag
      }
      break
    }
    case "positionAttributes": {
      filter = update(filter, { positionAttributes: { $splice: [[id, 1]] } })
      if (isEmpty(filter.positionAttributes)) {
        delete filter.positionAttributes
      }
      break
    }
  }

  if (keys(filter).length === 0) {
    filter = null
  }

  return filter
}

export function computeUpdatedFilters(filters) {
  // Exit early if no filters
  if (isEmpty(filters)) {
    return { filter: null, addedFilters: [] }
  }

  let newFilter = {}

  for (const filter of filters) {
    const { filterOn: { type, dataType, value: id, contentSetId }, operator, values } = filter
    newFilter = computeAddFilter(newFilter, type, dataType, id, operator, values, contentSetId)
  }
  return { filter: newFilter, addedFilters: (filters ?? []).map(f => ({ ...f, isExisting: true })) }
}

export function computeAddedFilters(filter, filterOns, additionalParams = {}) {
  if (isEmpty(filter)) {
    return EMPTY_ARRAY
  }

  const addedFilters = []
  const filterKeys = Object.keys(filter)

  filterKeys.forEach((field, keyIndex) => {
    const filterType = FILTER_TYPES.find(ft => ft.field === field)
    const fieldValue = filter[field]

    if (filterType && (isBoolean(fieldValue) || (fieldValue && fieldValue.length !== 0))) {
      const { fetcher } = filterType
      const filterMetadata = []

      fetcher(filterMetadata, {
        filter: filter[field],
        filterType,
        id: field,
        ...additionalParams,
      })

      filterMetadata.forEach(({ type, id }) => {
        const editing = { filterType: type, id }
        const computedFilter = computeRawFilter(filter, editing, filterOns, additionalParams.securitiesDescriptors)

        if (computedFilter) {
          addedFilters.push({
            id,
            type,
            filterOn: computedFilter.filter?.filterOn,
            operator: computedFilter.filter?.operator,
            values: computedFilter.filter?.values || [],
            isUnsupported: computedFilter.isUnsupported || false,
            isExisting: true,
            order: keyIndex,
          })
        }
      })
    }
  })

  return addedFilters.sort((a, b) => a.order - b.order)
}
